/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import axios, {AxiosError} from 'axios'

import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import Astericsign from '../../user/components/forms/Astericsign'
import {
  firstNameValidation,
  lastNameValidation,
} from '../../user/components/forms/helper/ValidationHelperComponent'
import MetaTags from '../../../meta/MetaTags'
import {API_ENDPOINTS} from '../../../constants/apiConstants'

const initialValues = {
  first_name: '',
  last_name: '',
  password: '',
  confirmPassword: '',
}

const validationSchema = Yup.object().shape({
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  password: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(50, 'Maximum 50 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
})

export function Registration() {
  const [checking, setChecking] = useState(true) // for invite status check
  const [loading, setLoading] = useState(false) // for form submit
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  // 1) On mount — fetch invitation
  useEffect(() => {
    if (!token) {
      toast.error('Invitation token missing.')
      navigate('/error/404', {replace: true})
      return
    }

    axios
      .get<{
        status: string // wrapper status
        data: {status: string} // invite object with its status field
      }>(API_ENDPOINTS.GET_BILLER_INVITATION(token))
      .then((resp) => {
        const inviteStatus = resp.data.data.status // ← note the extra `.data`
        if (inviteStatus === 'accepted') {
          toast.info('This invitation is already accepted. Please log in.')
          navigate('/login', {replace: true})
        } else {
          setChecking(false)
        }
      })
      .catch(() => {
        toast.error('Invalid or expired invitation.')
        navigate('/error/404', {replace: true})
      })
  }, [token, navigate])

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setStatus(undefined)

      try {
        await axios.post<{message: string}>(API_ENDPOINTS.REGISTER_BILLING_PERSONNEL_VIA_TOKEN(), {
          token,
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
        })

        toast.success('Your account has been created! You can now log in.')
        navigate('/login')
      } catch (err) {
        const axiosErr = err as AxiosError<{message: string}>
        setStatus(axiosErr.response?.data?.message || 'Registration failed.')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  const preventNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key >= '0' && e.key <= '9') e.preventDefault()
  }

  // 2) While we’re checking invitation status, don’t show the form
  if (checking) {
    return (
      <div className='text-center py-10'>
        <MetaTags title='Checking Invitation…' />
        <span>Checking invitation…</span>
      </div>
    )
  }

  // 3) Render the form once we've verified it's pending
  return (
    <>
      <MetaTags title='Complete Your Registration' />

      <div className='text-center mb-11'>
        <h1 className='fw-bolder mb-3'>Finish Signing Up</h1>
        <div className='text-gray-500 fw-semibold fs-6'>Please set your name & password</div>
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        onSubmit={formik.handleSubmit}
      >
        {/* First Name */}
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder fs-6'>
            First Name <Astericsign />
          </label>
          <input
            type='text'
            placeholder='First name'
            autoComplete='off'
            onKeyDown={preventNumbers}
            {...formik.getFieldProps('first_name')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.first_name && formik.errors.first_name,
              },
              {
                'is-valid': formik.touched.first_name && !formik.errors.first_name,
              }
            )}
          />
          {formik.touched.first_name && formik.errors.first_name && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.first_name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Last Name */}
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder fs-6'>
            Last Name <Astericsign />
          </label>
          <input
            type='text'
            placeholder='Last name'
            autoComplete='off'
            onKeyDown={preventNumbers}
            {...formik.getFieldProps('last_name')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.last_name && formik.errors.last_name,
              },
              {
                'is-valid': formik.touched.last_name && !formik.errors.last_name,
              }
            )}
          />
          {formik.touched.last_name && formik.errors.last_name && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.last_name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Password */}
        <div className='fv-row mb-8' data-kt-password-meter='true'>
          <div className='mb-1'>
            <label className='form-label fw-bolder fs-6'>
              Password <Astericsign />
            </label>
            <div className='position-relative mb-3'>
              <input
                type='password'
                placeholder='Password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Password strength meter */}
            <div
              className='d-flex align-items-center mb-3'
              data-kt-password-meter-control='highlight'
            >
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
            </div>
          </div>
          <div className='text-muted'>
            Use 8 or more characters with a mix of letters, numbers & symbols.
          </div>
        </div>

        {/* Confirm Password */}
        <div className='fv-row mb-5'>
          <label className='form-label fw-bolder fs-6'>
            Confirm Password <Astericsign />
          </label>
          <input
            type='password'
            placeholder='Confirm password'
            autoComplete='off'
            {...formik.getFieldProps('confirmPassword')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword,
              },
              {
                'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
              }
            )}
          />
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.confirmPassword}</span>
              </div>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className='text-center'>
          <button
            type='submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Set Password & Continue</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            )}
          </button>
        </div>
        <div className='text-center mb-4 text-black'>
          Already registered?{' '}
          <Link to='/login' className='text-primary'>
            Login
          </Link>
        </div>
      </form>
    </>
  )
}
